import React, { useCallback, useMemo, useState } from "react";

import { useWindowSize } from "react-use";
import { Trans, useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TablePagination } from "@mui/material";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MUTATION_CHECKOUT_OWNER } from "../../config/graphql/mutation";

import Modal from "../Modal";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useOverview } from "../../lib/hooks/useQueryBoard";
import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import MoveLeft from "../Animations/MoveLeft";

const CheckoutButton = styled(Button)(() => ({
  fontSize: "2.5rem",
  width: "15rem",
  marginLeft: "2rem",
}));

interface ListProps {
  rows: {
    id: string;
    fullName: string;
    type: AccessEventType;
    checkinAt: string;
    host: string;
  }[];
}

const List = (props: ListProps) => {
  const { rows } = props;

  const { fields } = useOverview();

  const { width, height } = useWindowSize();

  const rowsPerPage = width < height ? 7 : 2;

  const { t } = useTranslation(["overview"]);

  const [page, setPage] = useState<number>(0);

  const [open, setOpen] = useState<boolean>(true);

  const [confirmModalId, setConfirmModalId] = useState<string>();

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const numberOfPages = Math.ceil(rows.length / rowsPerPage);

  const { status: requestStatus, mutate: onCheckoutOwner } = useMutation(
    async (id: string) => {
      return client.request<{ checkOutOwner: IAccessEvent }, { id: string }>(
        MUTATION_CHECKOUT_OWNER,
        { id },
      );
    },
  );

  const status = useDebounceStatus(requestStatus, 1500);

  const queryClient = useQueryClient();

  const onDismiss = useCallback(() => {
    if (status === "success" || status === "error") {
      setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["currentlyCheckedIn"],
        refetchType: "all",
      });
    }
  }, [status]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 4000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  return (
    <>
      <Table>
        <TableHead>
          <MoveLeft delay={0.1}>
            <TableRow component={motion.tr}>
              {fields?.includes("NAME") && (
                <TableCell>{t(`overview:table.head.fullName`)}</TableCell>
              )}
              {fields?.includes("TYPE") && (
                <TableCell>{t(`overview:table.head.type`)}</TableCell>
              )}
              {fields?.includes("CHECKIN_TIME") && (
                <TableCell>{t(`overview:table.head.checkedinAt`)}</TableCell>
              )}
              {fields?.includes("HOST") && (
                <TableCell>{t(`overview:table.head.host`)}</TableCell>
              )}
              <TableCell />
            </TableRow>
          </MoveLeft>
        </TableHead>
        <TableBody>
          <AnimatePresence>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, i) => (
                <MoveLeft key={row.id} delay={0.15 + i * 0.05}>
                  <TableRow component={motion.tr}>
                    {fields?.includes("NAME") && (
                      <TableCell
                        component="th"
                        scope="row"
                        className="text-secondary"
                      >
                        {row.fullName}
                      </TableCell>
                    )}
                    {fields?.includes("TYPE") && (
                      <TableCell>
                        {t(`overview:table.type.${row.type}`)}
                      </TableCell>
                    )}
                    {fields?.includes("CHECKIN_TIME") && (
                      <TableCell>{row.checkinAt}</TableCell>
                    )}
                    {fields?.includes("HOST") && (
                      <TableCell>{row.host || "/"}</TableCell>
                    )}
                    {fields?.includes("CHECKOUT") && (
                      <TableCell>
                        <CheckoutButton
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setConfirmModalId(row.id);
                          }}
                        >
                          {t("home:checkOut")}
                        </CheckoutButton>
                      </TableCell>
                    )}
                    <Dialog
                      open={confirmModalId === row.id}
                      onClose={() => {
                        setConfirmModalId(undefined);
                      }}
                      sx={{
                        ".MuiPaper-root": {
                          paddingX: 5,
                          paddingY: 3,
                          animation:
                            "moveUp 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                        },
                      }}
                    >
                      <DialogTitle className="text-center mb-5" fontSize={32}>
                        {t("overview:confirmModal.title", {
                          fullName: row.fullName,
                        })}
                      </DialogTitle>
                      <DialogActions className="justify-content-center">
                        <Button
                          variant="contained"
                          size="small"
                          className="mx-4 px-5"
                          onClick={() => {
                            setConfirmModalId(undefined);
                          }}
                        >
                          {t("overview:confirmModal.cancel")}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          className="px-5"
                          onClick={() => {
                            onCheckoutOwner(row.id);
                            setConfirmModalId(undefined);
                            setOpen(true);
                          }}
                        >
                          {t("overview:confirmModal.checkout")}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </TableRow>
                </MoveLeft>
              ))}
          </AnimatePresence>
        </TableBody>
      </Table>
      {numberOfPages !== 1 && (
        <div className="d-flex align-items-center mt-5 pt-3">
          <div style={{ fontSize: "2.7rem" }}>
            {t("overview:page")}{" "}
            <span className="ms-4">
              {page + 1} / {numberOfPages}
            </span>
          </div>
          <TablePagination
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </div>
      )}
      <AnimatePresence mode="wait">
        {open && status !== "idle" && (
          <Modal
            title={
              <Trans i18nKey={`overview:checkoutStatus.${status}.title`} />
            }
            message={
              <Trans i18nKey={`overview:checkoutStatus.${status}.message`} />
            }
            status={status}
            onDismiss={onDismiss}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(List);
